import { createContext, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';

import { useOnboardingQueryParams } from './useOnboardingQueryParams';

/**
 * Map of guidance step selector -> initial value for data-guidance-valid.
 * When a step element mounts and its selector is in this map, it will
 * receive the corresponding data-guidance-valid value (e.g. 'false').
 * Used by parents (e.g. EditFlagTargeting) to set initial validation state
 * for steps that render inside dialogs, where useEffect-based setup runs
 * before the dialog is in the DOM.
 */
export type GuidanceInitialValidMap = Record<string, string> | undefined;

/**
 * Map of logical key -> guidance step selector (compound selector string).
 * Allows providers to register named steps (e.g. "save", "confirmation") so downstream
 * components can look them up by key. Extensible for future step types.
 */
export type GuidanceStepsMap = Record<string, string>;

/**
 * Extended context value that includes optional guidance step selectors keyed by name.
 * Downstream components look up steps by key (e.g. useGuidanceStep('save')).
 */
export type GuidanceContextValue = {
  /** Map of step selector -> initial data-guidance-valid value */
  initialValidMap: GuidanceInitialValidMap;
  /** Map of logical key -> guidance step selector (e.g. { save: 'flow:step3', confirmation: 'flow:step4' }) */
  guidanceSteps?: GuidanceStepsMap;
};

/** Provider value: either the map only (legacy) or the full context value */
export type GuidanceInitialValidProviderValue = GuidanceInitialValidMap | GuidanceContextValue;

const GuidanceInitialValidContext = createContext<GuidanceInitialValidProviderValue>(undefined);

export const GuidanceInitialValidProvider = GuidanceInitialValidContext.Provider;

function getInitialValidMap(value: GuidanceInitialValidProviderValue): GuidanceInitialValidMap {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'object' && 'initialValidMap' in value) {
    return (value as GuidanceContextValue).initialValidMap;
  }
  return value as GuidanceInitialValidMap;
}

export function useGuidanceInitialValid(): GuidanceInitialValidMap {
  const value = useContext(GuidanceInitialValidContext);
  return getInitialValidMap(value);
}

/**
 * Returns the initial data-guidance-valid value for a step, if the parent
 * provided one for this step selector.
 */
export function useInitialGuidanceValidForStep(stepSelector: string | undefined): string | undefined {
  const map = useGuidanceInitialValid();
  if (!stepSelector || !map) {
    return undefined;
  }
  return map[stepSelector];
}

/**
 * Props to spread onto an element that is a guidance step target.
 * Only includes keys when values are defined.
 */
export type GuidanceStepProps = {
  'data-guidance-step'?: string;
  'data-guidance-valid'?: string;
};

/**
 * Returns props to spread onto a guidance step element (e.g. a save button).
 * Combines the step selector and initial validation value so callers don't
 * need to use useInitialGuidanceValidForStep separately.
 *
 * @param guidanceStep - Compound step selector (e.g. from useGuidanceStep('save') or props)
 * @returns Object with data-guidance-step and/or data-guidance-valid when defined
 */
export function useGuidanceStepProps(guidanceStep: string | undefined): GuidanceStepProps {
  const initialGuidanceValid = useInitialGuidanceValidForStep(guidanceStep);
  return {
    ...(guidanceStep !== undefined && { 'data-guidance-step': guidanceStep }),
    ...(initialGuidanceValid !== undefined && { 'data-guidance-valid': initialGuidanceValid }),
  };
}

/**
 * Returns the guidance step selector for the given key from context, when the provider
 * supplies a guidanceSteps map. Use for any named step (e.g. 'save', 'confirmation').
 */
export function useGuidanceStep(key: string): string | undefined {
  const value = useContext(GuidanceInitialValidContext);
  if (value === null || value === undefined || typeof value !== 'object' || !('initialValidMap' in value)) {
    return undefined;
  }
  const extended = value as GuidanceContextValue;
  return extended.guidanceSteps?.[key];
}

/**
 * Returns the full guidance steps map from context when the provider supplies it.
 * Use when you need multiple steps or custom keys.
 */
export function useGuidanceStepsMap(): GuidanceStepsMap | undefined {
  const value = useContext(GuidanceInitialValidContext);
  if (value === null || value === undefined || typeof value !== 'object' || !('initialValidMap' in value)) {
    return undefined;
  }
  const extended = value as GuidanceContextValue;
  return extended.guidanceSteps;
}

/**
 * Returns save-button guidance step selectors from context (keys "save" and "confirmation").
 * Convenience hook for SplitSaveButton; other consumers can use useGuidanceStep(key).
 */
export function useSaveGuidanceSteps(): {
  guidanceStep: string | undefined;
  confirmationGuidanceStep: string | undefined;
} {
  const guidanceSteps = useGuidanceStepsMap();
  return {
    guidanceStep: guidanceSteps?.save,
    confirmationGuidanceStep: guidanceSteps?.confirmation,
  };
}

function markGuidanceStepValid(guidanceStep: string | undefined) {
  if (!guidanceStep) {
    return;
  }
  document
    .querySelector(`[data-guidance-step="${CSS.escape(guidanceStep)}"]`)
    ?.setAttribute('data-guidance-valid', 'true');
}

/**
 * Returns save-button guidance wiring and save-success lifecycle callbacks.
 */
export function useSaveGuidanceLifecycle(): {
  guidanceStep: string | undefined;
  onSaveSuccessBeforeClose: () => void;
} {
  const { guidanceStep, confirmationGuidanceStep } = useSaveGuidanceSteps();
  const params = useOnboardingQueryParams();
  const navigate = useNavigate();

  const onSaveSuccessBeforeClose = useCallback(() => {
    // When the save just confirmed is the onboarding flow's "confirm save" step,
    // advance the flow imperatively by bumping `step` in the URL. The guidance
    // popover derives its current step from the URL (useOnboardingQueryParams),
    // so this moves it forward directly.
    //
    // This replaces the previous `data-guidance-valid` attribute write, which
    // raced with modal unmount: `onClose()` removed the confirm element from the
    // DOM before useValidationObserver's MutationObserver (a microtask) could
    // observe the false→true flip, so the step 4 → 5 advance never fired.
    if (params && `${params.onboardingFlow}:${params.selector}` === confirmationGuidanceStep) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('step', String(params.stepIndex + 1));
      void navigate({ search: searchParams.toString() }, { replace: true });
      return;
    }

    // No active onboarding step matched this save: preserve the prior best-effort
    // attribute write for any non-URL-driven consumer.
    markGuidanceStepValid(confirmationGuidanceStep);
  }, [params, confirmationGuidanceStep, navigate]);

  return { guidanceStep, onSaveSuccessBeforeClose };
}
